import * as PresetSlice from '@stores/graphPresetSlice';

/**
 * Action creator to add a new data graph to Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {Object} formik - The Formik context object for form management.
 */
export const addNewDataGraph = (graphPresetId, formik) => (dispatch, getState) => {
  // Dispatch the action to add the new DataGraph to Redux
  dispatch(PresetSlice.addNewDataGraph({ graphPresetId }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to move a data graph within Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} displayOrder - The display order of the data graph to be moved.
 * @param {string} direction - The direction of movement ('up' or 'down').
 * @param {Object} formik - The Formik context object for form management.
 */
export const moveDataGraph = (graphPresetId, displayOrder, direction, formik) => (dispatch, getState) => {
  // Dispatch the action to move the data graph within Redux
  dispatch(PresetSlice.moveDataGraph({ graphPresetId, displayOrder, direction }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to remove a data graph from Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} displayOrder - The display order of the data graph to be removed.
 * @param {Object} formik - The Formik context object for form management.
 */
export const removeDataGraph = (graphPresetId, displayOrder, formik) => (dispatch, getState) => {
  // Dispatch the action to remove the data graph from Redux
  dispatch(PresetSlice.removeDataGraph({ graphPresetId, displayOrder }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to add a new weather station filter to Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {Object} formik - The Formik context object for form management.
 */
export const addNewGraphFilter = (graphPresetId, displayOrder, formik) => (dispatch, getState) => {
  // Dispatch the action to add the new weather station filter to Redux
  dispatch(PresetSlice.addNewGraphFilter({ graphPresetId, displayOrder }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to remove a weather station filter from Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} weatherStationId - The ID of the weather station to be removed.
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {Object} formik - The Formik context object for form management.
 */
export const removeGraphFilter = (graphPresetId, weatherStationId, displayOrder, formik) => (dispatch, getState) => {
  // Dispatch the action to remove the weather station filter from Redux
  dispatch(PresetSlice.removeGraphFilter({ graphPresetId, weatherStationId, displayOrder }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to add a new dataset to a weather station filter in Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} weatherStationId - The ID of the weather station associated with the dataset.
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {Object} formik - The Formik context object for form management.
 */
export const addDataset = (graphPresetId, weatherStationId, displayOrder, formik) => (dispatch, getState) => {
  // Dispatch the action to add the new dataset to Redux
  dispatch(PresetSlice.addDataset({ graphPresetId, weatherStationId, displayOrder }));

  // Retrieve the updated Redux state
  const state = getState();

  // Use setFieldValue to update the Formik form state
  formik.setFieldValue('DataGraphs', [
    ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
  ]);
};

/**
 * Action creator to remove a dataset from a weather station filter in Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} weatherStationId - The ID of the weather station associated with the dataset.
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {string} placementName - The name of the placement associated with the filter.
 * @param {number} observationTypeId - The observation type ID to be updated.
 * @param {Object} formik - The Formik context object for form management.
 */
export const removeDataset =
  (graphPresetId, weatherStationId, displayOrder, placementName, observationTypeId, formik) => (dispatch, getState) => {
    // Dispatch the action to add the new weather station filter to Redux
    dispatch(
      PresetSlice.removeDataset({ graphPresetId, weatherStationId, placementName, displayOrder, observationTypeId }),
    );

    // Retrieve the updated Redux state
    const state = getState();

    // Use setFieldValue to update the Formik form state
    formik.setFieldValue('DataGraphs', [
      ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
    ]);
  };

/**
 * Action creator to update the weather station filter for a specific graph preset in Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {number} weatherStationId - The ID of the current weather station filter.
 * @param {number} newWeatherStationId - The new weather station ID to set.
 * @param {Object} formik - The Formik context object for form management.
 */
export const updateGraphFilterWeatherStation =
  (graphPresetId, displayOrder, weatherStationId, newWeatherStationId, formik) => (dispatch, getState) => {
    // Dispatch the action to update the weather station filter in Redux
    dispatch(
      PresetSlice.updateGraphFilterWeatherStation({
        graphPresetId,
        displayOrder,
        weatherStationId,
        newWeatherStationId,
      }),
    );

    // Retrieve the updated Redux state
    const state = getState();

    // Use setFieldValue to update the Formik form state
    formik.setFieldValue('DataGraphs', [
      ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
    ]);
  };

/**
 * Action creator to update the observation type (TypeID) for a specific weather station filter within a graph preset in Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} weatherStationId - The ID of the associated weather station filter.
 * @param {number} displayOrder - The display order of the weather station filter group.
 * @param {string} placementName - The name of the placement associated with the filter.
 * @param {number} currentTypeId - The current observation type ID to be updated.
 * @param {number} newTypeId - The new observation type ID to set.
 * @param {Object} formik - The Formik context object for form management.
 */
export const updateGraphFilterTypeId =
  (
    graphPresetId,
    weatherStationId,
    displayOrder,
    placementName,
    prevPlacementName,
    currentTypeId,
    newTypeId,
    obsObject,
    formik,
  ) =>
  (dispatch, getState) => {
    // Dispatch the action to update the observation type ID in Redux
    dispatch(
      PresetSlice.updateGraphFilterTypeId({
        graphPresetId,
        weatherStationId,
        displayOrder,
        placementName,
        prevPlacementName,
        currentTypeId,
        newTypeId,
        obsObject,
      }),
    );

    // Retrieve the updated Redux state
    const state = getState();

    // Use setFieldValue to update the Formik form state
    formik.setFieldValue('DataGraphs', [
      ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
    ]);
  };

/**
 * Action creator to update the observation type (TypeID) for a specific weather station filter within a graph preset in Redux state and update the Formik form state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} weatherStationId - The ID of the associated weather station filter.
 * @param {number} displayOrder - The display order of the weather station filter group.
 * @param {number} typeId - The current observation type ID to be updated.
 * @param {string} colorValue - The color override value to be set.
 * @param {Object} formik - The Formik context object for form management.
 */
export const updateGraphFilterColorOverride =
  (graphPresetId, weatherStationId, displayOrder, typeId, datasetPrefix, placementName, colorValue, formik) =>
  (dispatch, getState) => {
    // Dispatch the action to update the color override value for the observation type in Redux
    dispatch(
      PresetSlice.updateGraphFilterColorOverride({
        graphPresetId,
        weatherStationId,
        displayOrder,
        typeId,
        datasetPrefix,
        placementName,
        colorValue,
      }),
    );

    // Retrieve the updated Redux state
    const state = getState();

    // Use setFieldValue to update the Formik form state
    formik.setFieldValue('DataGraphs', [
      ...state.graphPreset.graphPresets.find((preset) => preset.GraphPresetId === graphPresetId).DataGraphs,
    ]);
  };

/**
 * Action creator to update the name of a specific data graph within a graph preset in Redux state.
 *
 * @param {number} graphPresetId - The ID of the current graph preset.
 * @param {number} dataGraphIdx - The index of the data graph to be updated.
 * @param {string} newGraphName - The new name to set for the data graph.
 */
export const updateGraphName = (graphPresetId, dataGraphIdx, newGraphName) => (dispatch, getState) => {
  // Dispatch the action to update the data graph's name in Redux
  dispatch(
    PresetSlice.updateGraphName({
      graphPresetId,
      dataGraphIdx,
      newGraphName,
    }),
  );
};

/**
 * Saves a preset in the application state.
 *
 * @param {Object} presetData - The data of the preset to be saved.
 * @param {string} originalPresetId - The original ID of the preset (if it's being updated).
 * @param {Function} dispatch - A function for dispatching actions to the store.
 * @param {Function} getState - A function for accessing the current state of the store.
 */
export const savePreset = (presetData, originalPresetId) => (dispatch, getState) => {
  // Dispatch an action to save the graph preset with the provided data and original preset ID
  dispatch(
    PresetSlice.saveGraphPreset({
      presetData,
      originalPresetId,
    }),
  );
};

export const deletePreset = (graphPresetId) => (dispatch, getState) => {
  dispatch(
    PresetSlice.deletePreset({
      graphPresetId,
    }),
  );
};
